import { useAuth0 } from '@auth0/auth0-react';
import * as d3 from 'd3';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useRef, useEffect, useContext, useState, useMemo } from 'react';
import { InfiniteData } from 'react-query';
import { useTheme } from 'styled-components';

import { useAnalytics } from '../../analytics';
import { Role } from '../../constants/auth';
import { monitorConfig } from '../../constants/page-configs';
import { AnalyzeContext } from '../../context/analyze-context';
import { AppContext } from '../../context/app-context';
import { useRoles } from '../../context/authorization-context';
import { MonitorContext } from '../../context/monitor-context';
import { useMonitorFilteringContext } from '../../context/monitor-filtering-context';
import { useTenants } from '../../hooks/tenants';
import { Analyze } from '../../pages/routes';
import {
  handleTooltipMouseMove,
  handleTooltipMouseOut,
  handleTooltipMouseOver,
} from '../analyze-charts/utils/tooltips';

import { StyledChart } from './styles';

import { AnomaliesResult, Anomaly, LabeledEvent, SVGDefsSelection } from '@controlrooms/models';
import { SeverityFilter } from '@controlrooms/models';
import { TimeUtils } from '@controlrooms/utils';
import { buildCells, buildGroups, buildLabeledEventGroups } from '@controlrooms/utils';
import { buildNoData, buildErrors } from '@controlrooms/utils';
import { appendFilters, setCellsVisibilityByClassName } from '@controlrooms/utils';
import { appendGradients } from '@controlrooms/utils';

dayjs.extend(utc);

// interface ZoomEvent {
//   transform:
//     | string
//     | number
//     | boolean
//     | readonly (string | number)[]
//     | d3.ValueFn<
//         d3.BaseType,
//         unknown,
//         string | number | boolean | readonly (string | number)[] | null
//       >
//     | null;
// }

export const Heatmap: React.FC<{
  folderId: number;
  heatmapData: InfiniteData<AnomaliesResult> | undefined;
  labeledEvents?: LabeledEvent[];
  interval: number;
  handleAnalyzeLink: () => void;
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  wideSpacing: boolean;
}> = ({ folderId, heatmapData, labeledEvents, interval, handleAnalyzeLink, wideSpacing }) => {
  // Heatmap data is an infinite result but currently referencing only page 0 (fetch prev/next not implemented)
  const chartData = heatmapData?.pages[0]?.result?.[folderId]?.anomalies;
  const limitsExceeded = heatmapData?.pages[0]?.result?.[folderId]?.limits_exceeded;

  const { userRoles } = useRoles();
  const { user } = useAuth0();

  const { currentTenant } = useTenants();
  const { track } = useAnalytics();
  const tenantConfig = currentTenant?.preferences;

  const { labelTypesFilter } = useMonitorFilteringContext();

  const {
    monitorTimeSelection: { startTime, endTime, timezone },
  } = useContext(AppContext);

  const {
    severityFilter,
    groupTooltipData,
    setGroupTooltipData,
    setLabelTooltipData,
    showMonitorLimits,
  } = useContext(MonitorContext);

  const { showAnalyzeLimits } = useContext(AnalyzeContext);
  const svgRef = useRef(null);
  const theme = useTheme();

  const { chartMargin } = monitorConfig;

  // store scrollable width
  const [height, setHeight] = useState<number>(500);

  const rowHeight = 24;

  const anomalyData = useMemo(() => {
    return chartData?.filter((a: Anomaly) => a.value > 0) ?? ([] as Array<Anomaly>);
  }, [chartData]);

  const errorData = useMemo(() => {
    return chartData?.filter((a: Anomaly) => a.value < 0) ?? ([] as Array<Anomaly>);
  }, [chartData]);

  useEffect(() => {
    if ((!chartData || chartData.length === 0) && (!limitsExceeded || limitsExceeded.length === 0))
      return;

    const ChartWrapper = d3.select(`.heatmap[data-id="${folderId}"]`);

    if (!ChartWrapper) return;

    const { width: wrapperWidth, height: wrapperHeight } =
      (ChartWrapper.node() as HTMLElement)?.getBoundingClientRect() ?? {};
    const ctrWidth = wrapperWidth - chartMargin.left + 15;

    setHeight(rowHeight);

    const svgEl = d3.select(svgRef.current).attr('viewBox', `0 0 ${wrapperWidth} ${wrapperHeight}`);

    // Clear svg content before adding new elements
    svgEl.selectAll('*').remove();
    // add Tooltip container
    svgEl
      .append('rect')
      .attr('width', ctrWidth)
      .attr('height', wrapperHeight)
      .style('transform', `translate(${chartMargin.left}px, 0)`)
      .style('opacity', 0)
      .on('mouseout', () =>
        // on mouse out hide line, circles and text
        handleTooltipMouseOut('monitor'),
      )
      .on('mouseover', () =>
        // on mouse in show line, circles and text
        handleTooltipMouseOver('monitor', { folder: folderId }),
      )
      .on('touchmouse mousemove', (e: MouseEvent) => {
        handleTooltipMouseMove('monitor', e, xScale, timezone, theme, { tenantConfig });
      })
      .classed('heatmap-chart-rect', true);

    const chart = svgEl
      .append('g')
      .attr('width', '100%')
      .attr('height', height)
      .attr('preserveAspectRatio', 'none')
      .attr('viewBox', `0 0 ${wrapperWidth} ${height}`);

    chart.style('transform', 'translate(0px, 2px)');

    if (wideSpacing) chart.style('transform', 'translate(0px, 18px)');

    const svgDefs = svgEl.append('defs') as unknown as SVGDefsSelection;

    // add Gradients to svg defs
    appendGradients(svgDefs, theme);

    // add filters to svg defs - drop shadows
    appendFilters(svgDefs);

    // remove all tooltips
    ChartWrapper.selectAll('.tooltip-content').remove();

    // add tooltip
    ChartWrapper.append('div')
      .datum(chartData)
      .attr('class', 'tooltip-content')
      .attr('id', `tooltip-content-${folderId}`);

    // Clipping
    svgDefs
      .append('clipPath')
      .attr('id', 'clip')
      .append('rect')
      .attr('x', chartMargin.left)
      .attr('y', 0)
      .attr('width', ctrWidth)
      .attr('height', wrapperHeight);

    // append a container for margin spacing
    const ctr = chart
      .append('g')
      .classed('folder-container ctr', true)
      .attr('transform', `translate(${chartMargin.left},0)`)
      .on('mouseenter', () => d3.select(`.folder-row-${folderId}`).classed('hover', true))
      .on('mouseleave', () =>
        d3.select(`.folder-row-${folderId}`).classed('hover', false),
      ) as unknown as SVGDefsSelection;

    const xScale = d3
      .scaleUtc()
      .domain([startTime.valueOf(), endTime.valueOf()])
      .range([0, ctrWidth]);

    buildCells(ctr, anomalyData, xScale, tenantConfig?.monitorAnomalyThresholds, {
      theme,
      height: rowHeight - 8,
      interval,
      rowHeight,
      limitData: limitsExceeded,
      labelTypesFilter,
      labeledEvents,
      showMonitorLimits,
      showAnalyzeLimits,
      folderId,
    }).attr('data-time', (d) => TimeUtils.toUtc(d.time).format('HH:mm'));

    //After building cells, use CSS to hide them based on the setting of the Severity Filter
    switch (severityFilter) {
      case SeverityFilter.LOW: {
        setCellsVisibilityByClassName(ctr, 'g1', 'block');
        setCellsVisibilityByClassName(ctr, 'g2', 'block');
        setCellsVisibilityByClassName(ctr, 'g3', 'block');
        break;
      }
      case SeverityFilter.MEDIUM: {
        setCellsVisibilityByClassName(ctr, 'g1', 'none');
        setCellsVisibilityByClassName(ctr, 'g2', 'block');
        setCellsVisibilityByClassName(ctr, 'g3', 'block');
        break;
      }
      case SeverityFilter.HIGH: {
        setCellsVisibilityByClassName(ctr, 'g1', 'none');
        setCellsVisibilityByClassName(ctr, 'g2', 'none');
        setCellsVisibilityByClassName(ctr, 'g3', 'block');
        break;
      }
    }

    if (user?.email_verified && userRoles.includes(Role.GLOBAL_LABEL_EDITOR)) {
      buildGroups(ctr, anomalyData, xScale, folderId, {
        height: rowHeight - 8,
        interval,
        rowHeight,
        groupTooltipData,
        setGroupTooltipData,
        theme,
      });
    }

    const getFolderEvents = () => {
      const thisFolderEvents: LabeledEvent[] = [];
      labeledEvents?.forEach((le) => {
        if (le.process_id === folderId) {
          thisFolderEvents.push(le);
        }
      });
      return thisFolderEvents;
    };

    buildLabeledEventGroups(ctr, getFolderEvents(), xScale, folderId, {
      theme,
      height,
      rowHeight,
      setGroupTooltipData,
      setLabelTooltipData,
    });

    const errorContainer = ctr.append('g').classed('error-container', true);
    buildNoData(errorContainer, errorData, folderId, xScale, tenantConfig, {
      errorHeight: 8,
      interval,
      noDataHeight: 2,
      rowHeight,
    });

    buildErrors(errorContainer, errorData, folderId, xScale, tenantConfig, {
      errorHeight: 12,
    });
  }, [
    wideSpacing,
    anomalyData,
    chartData,
    limitsExceeded,
    chartMargin.left,
    chartMargin.right,
    endTime,
    errorData,
    folderId,
    height,
    interval,
    startTime,
    tenantConfig,
    theme,
    timezone,
    rowHeight,
    groupTooltipData,
    setGroupTooltipData,
    setLabelTooltipData,
    severityFilter,
    labeledEvents,
    labelTypesFilter,
    showMonitorLimits,
    showAnalyzeLimits,
    user,
    userRoles,
  ]);

  return (
    <StyledChart
      className="heatmap"
      data-id={folderId}
      onClick={() => {
        track('Monitor - Go to Analyze via Heatmap', {
          heatmap: 'clicked',
          folderId: folderId,
        });
        handleAnalyzeLink();
      }}
      onMouseOver={Analyze.preload}
    >
      {chartData || limitsExceeded ? <svg ref={svgRef} /> : <div className="empty" />}
    </StyledChart>
  );
};
